import estimationService from "../../services/estimationService";
import leadService from "../../services/leadService";

const getDefaultState = () => {
  return {
    valid: false,
    hasSolarPanels: false,
    solarPanelsPower: null,
    meterType: [],
    emptyHouse: false,
    knowConsumption: true,
    consumptions: [
      {
        meterType: "day",
        value: null
      },
      {
        meterType: "night",
        value: null
      },
      {
        meterType: "mono",
        value: null
      },
      {
        meterType: "exclusiveNight",
        value: null
      },
      {
        meterType: "gas",
        value: null
      }
    ],
    occupation: "evening_weekend",
    houseSize: 100,
    people: 2,
    heating: null,
    water: null,
    dishwasher: false,
    dryer: false,
    washingMachine: false
  };
};

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    setValid(state, value) {
      state.valid = value;
    },
    setMeterType(state, value) {
      state.meterType = value;
    },
    setHasSolarPanels(state, value) {
      state.hasSolarPanels = value;
    },
    setSolarPanelsPower(state, value) {
      state.solarPanelsPower = value;
    },
    setEmptyHouse(state, value) {
      state.emptyHouse = value;
    },
    setKnowConsumption(state, value) {
      state.knowConsumption = value;
    },
    setConsumptions(state, value) {
      state.consumptions = value;
    },
    setPeople(state, value) {
      state.people = value;
    },
    increasePeople(state) {
      if (state.people < 8) state.people += 1;
    },
    decreasePeople(state) {
      if (state.people > 1) state.people -= 1;
    },
    increaseHouseSize(state) {
      if (state.houseSize < 250) {
        state.houseSize =
          state.houseSize < 100 ? state.houseSize + 25 : state.houseSize + 50;
      }
    },
    setHouseSize(state, value) {
      state.houseSize = value;
    },
    decreaseHouseSize(state) {
      if (state.houseSize > 25) {
        state.houseSize =
          state.houseSize > 100 ? state.houseSize - 50 : state.houseSize - 25;
      }
    },
    setHeating(state, value) {
      state.heating = value;
    },
    setWater(state, value) {
      state.water = value;
    },
    setDishwasher(state, value) {
      state.dishwasher = value;
    },
    setDryer(state, value) {
      state.dryer = value;
    },
    setWashingMachine(state, value) {
      state.washingMachine = value;
    },
    setOccupation(state, value) {
      state.occupation = value;
    },
    setToDefault(state, value) {
      state.occupation = "evening_weekend";
      state.houseSize = 100;
      state.people = 2;
      state.heating = null;
      state.water = null;
      state.dishwasher = false;
      state.dryer = false;
      state.washingMachine = false
    },
    reset(state) {
      Object.assign(state, getDefaultState());
    }
  },
  actions: {
    async getConsumption(context) {
      let estimationData = await context.dispatch("getEstimationData");

      if (!context.state.knowConsumption) {
        try {
          let response = await estimationService.getEstimation(estimationData);

          let consumptions = [
            {
              meterType: "day",
              value: null
            },
            {
              meterType: "night",
              value: null
            },
            {
              meterType: "mono",
              value: null
            },
            {
              meterType: "exclusiveNight",
              value: null
            },
            {
              meterType: "gas",
              value: null
            }
          ];

          if (response.electricity) {
            Object.keys(response.electricity).forEach(key => {
              let consumptionIndex = consumptions.indexOf(
                consumptions.find(consumption => consumption.meterType === key)
              );

              if (consumptionIndex >= 0)
                consumptions[consumptionIndex] = {
                  meterType: key,
                  value:
                    response.electricity[key] > 0
                      ? response.electricity[key]
                      : 1
                };
            });
          }

          if (response.gas) {
            let consumptionIndex = consumptions.indexOf(
              consumptions.find(consumption => consumption.meterType === "gas")
            );

            if (consumptionIndex >= 0)
              consumptions[consumptionIndex] = {
                meterType: "gas",
                value: response.gas.total > 0 ? response.gas.total : 1
              };
          }

          context.commit("setConsumptions", consumptions);
        } catch (error) {
          throw error;
        }
      }
    },
    getEstimationData(context) {
      let energies = [];
      let hasElec = context.rootGetters["energy/hasElec"];

      if (hasElec) energies.push("electricity");
      if (context.rootGetters["energy/hasGas"]) energies.push("gas");

      let pricingOption = null;
      let exclusiveNight = false;

      if (hasElec) {
        pricingOption = context.state.meterType.includes("night")
          ? "HPHC"
          : "BASE";

        exclusiveNight = context.state.meterType.includes("exclusiveNight");
      }

      let mappingHouseSizing = {
        25: "between_0_and_25",
        50: "between_26_and_50",
        75: "between_51_and_75",
        100: "between_76_and_100",
        150: "between_101_and_150",
        200: "between_151_and_200",
        250: "more_200"
      };

      let houseSize = mappingHouseSizing[context.state.houseSize];

      let estimationData = {
        energies: energies,
        emptyHouse: context.state.emptyHouse,
        pricingOption: pricingOption,
        exclusiveNight: exclusiveNight,
        people: context.state.people,
        houseSize: houseSize,
        occupation: context.state.occupation,
        heating: context.state.heating,
        water: context.state.water,
        dishwasher: context.state.dishwasher,
        dryer: context.state.dryer,
        washingMachine: context.state.washingMachine,
        solarPanelsPower: context.state.hasSolarPanels
          ? context.state.solarPanelsPower
          : 0
      };

      return estimationData;
    },
    async save(context) {
      let knowConsumption = context.state.knowConsumption;
      let data = {
        uuid: context.rootState.common.uuid,
        pricingOption: context.state.meterType.includes("mono")
          ? "BASE"
          : "HPHC",
        solarPanelsPower: context.state.solarPanelsPower,
        emptyHouse: context.state.emptyHouse,
        knowConsumption: context.state.knowConsumption,
        people: !knowConsumption ? context.state.people : null,
        houseSize: !knowConsumption ? context.state.houseSize : null,
        occupation: !knowConsumption ? context.state.occupation : null,
        heating: !knowConsumption ? context.state.heating : null,
        water: !knowConsumption ? context.state.water : null,
        dryer: !knowConsumption ? context.state.dryer : null,
        washingMachine: !knowConsumption ? context.state.washingMachine : null,
        dishwasher: !knowConsumption ? context.state.dishwasher : null,
        consumptionElecBase: null,
        consumptionElecHigh: null,
        consumptionElecLow: null,
        consumptionElecNight: null,
        consumptionGas: null
      };

      context.getters["activeConsumptions"].forEach(consumption => {
        switch (consumption.meterType) {
          case "mono":
            data["consumptionElecBase"] = consumption.value;
            break;
          case "day":
            data["consumptionElecHigh"] = consumption.value;
            break;
          case "night":
            data["consumptionElecLow"] = consumption.value;
            break;
          case "exclusiveNight":
            data["consumptionElecNight"] = consumption.value;
            break;
          case "gas":
            data["consumptionGas"] = consumption.value;
            break;
        }
      });

      try {
        await leadService.save(data);
      } catch (error) {
        throw error;
      }
    }
  },
  getters: {
    elecMeterTypes: state => {
      let meterTypes = [...state.meterType];

      let index = meterTypes.indexOf("mono");
      if (index >= 0) meterTypes[index] = "day";

      meterTypes = meterTypes.filter(meterType => meterType !== "gas");

      return meterTypes;
    },
    activeConsumptions: state => {
      return state.consumptions.filter(consumption =>
        state.meterType.includes(consumption.meterType)
      );
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/consumption.js